// DEPENDENCIES
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

// CSS
import "./DropboxInterrogator.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Collapse,
	CircularProgress,
	Divider,
	FormControlLabel,
	FormGroup,
	LinearProgress,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";

// MUI ICONS
import {
	ArrowUpward as ArrowUpwardIcon,
	Refresh as RefreshIcon,
	PlayArrow as PlayArrowIcon,
	Cancel as CancelIcon,
	BugReport as BugReportIcon,
	ExpandMore as ExpandMoreIcon,
	ExpandLess as ExpandLessIcon,
	Folder as FolderIcon,
	Description as DescriptionIcon,
	Clear as ClearIcon,
} from "@mui/icons-material";

// CONSTANTS
const FILTER_KEY = "dropbox_tree_filter";
const FILE_VIS_KEY = "dropbox_tree_file_visibility";

const initialTypeFilters = {
	all: true,
	doc: false,
	xls: false,
	pdf: false,
	jpg: false,
	pst: false,
	mov: false,
};

const normalizePath = (path) => {
	if (!path) return "";
	let normalized = String(path).trim();
	if (!normalized) return "";
	normalized = normalized.startsWith("/") ? normalized : `/${normalized}`;
	normalized = normalized.replace(/\/{2,}/g, "/");
	if (normalized.length > 1) {
		normalized = normalized.replace(/\/+$/g, "");
	}
	return normalized;
};

const getParentPath = (path) => {
	const normalized = normalizePath(path);
	if (!normalized || normalized === "/") return "";
	const parts = normalized.split("/").filter(Boolean);
	parts.pop();
	return parts.length ? `/${parts.join("/")}` : "";
};

const displayPath = (path) => {
	const normalized = normalizePath(path);
	return normalized || "/";
};

const displayPathForInput = (path) => {
	const shown = displayPath(path);
	if (shown === "/") return "/";
	return shown.endsWith("/") ? shown : `${shown}/`;
};

const toDropboxApiPath = (path) => {
	const normalized = normalizePath(path);
	return normalized === "/" ? "" : normalized;
};

const pathBasename = (path) => {
	const normalized = displayPath(path);
	const parts = normalized.split("/").filter(Boolean);
	return parts.length ? parts[parts.length - 1] : "/";
};

const classifyFile = (name) => {
	const lower = (name || "").toLowerCase();
	if (lower.endsWith(".docx") || lower.endsWith(".doc")) return "doc";
	if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) return "xls";
	if (lower.endsWith(".pdf")) return "pdf";
	if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "jpg";
	if (lower.endsWith(".pst")) return "pst";
	if (lower.endsWith(".mov") || lower.endsWith(".mp4")) return "mov";
	return "other";
};

const countTypes = (entries) => {
	const counts = {
		doc: { count: 0, bytes: 0 },
		xls: { count: 0, bytes: 0 },
		pdf: { count: 0, bytes: 0 },
		jpg: { count: 0, bytes: 0 },
		pst: { count: 0, bytes: 0 },
		mov: { count: 0, bytes: 0 },
	};

	entries.forEach((entry) => {
		if (entry[".tag"] !== "file") return;
		const kind = classifyFile(entry.name);
		const size = typeof entry.size === "number" ? entry.size : 0;
		if (counts[kind]) {
			counts[kind].count += 1;
			counts[kind].bytes += size;
		}
	});

	return counts;
};

const formatBytes = (bytes) => {
	if (!bytes || bytes <= 0) return "0B";
	const units = ["B", "KB", "MB", "GB", "TB"];
	let val = bytes;
	let idx = 0;
	while (val >= 1024 && idx < units.length - 1) {
		val /= 1024;
		idx += 1;
	}
	const rounded = val >= 10 || idx === 0 ? val.toFixed(0) : val.toFixed(1);
	return `${rounded}${units[idx]}`;
};

const buildDropboxFolderUrl = (path) => {
	if (!path) return "https://www.dropbox.com/home";
	const normalized = normalizePath(path);
	const encoded = normalized
		.split("/")
		.map((segment) => encodeURIComponent(segment))
		.join("/");
	return `https://www.dropbox.com/home${encoded}`;
};

const buildDropboxPreviewUrl = (path) => {
	if (!path) return "https://www.dropbox.com/home";
	const normalized = normalizePath(path);
	const encoded = normalized
		.split("/")
		.map((segment) => encodeURIComponent(segment))
		.join("/");
	return `https://www.dropbox.com/preview${encoded}`;
};

const typeLabelMap = {
	doc: ".docx/.doc",
	xls: ".xlsx/.xls",
	pdf: ".pdf",
	jpg: ".jpg/.jpeg",
	pst: ".pst",
	mov: ".mov/.mp4",
};

const splitPathSegments = (path) =>
	normalizePath(path)
		.split("/")
		.filter(Boolean);

const isConstrainedRoot = (path) => {
	const segments = splitPathSegments(path);
	if (!segments.length) return false;
	const root = segments[0].toLowerCase();
	return root === "projects" || root === "archives";
};

const isAllowedPropertyLevelPath = (path) => {
	const segments = splitPathSegments(path);
	if (segments.length !== 3 && segments.length !== 4) return false;
	const root = segments[0].toLowerCase();
	return root === "projects" || root === "archives";
};

const allowedRootFolders = new Set(["projects", "archives"]);

// REACT COMPONENT
const DropboxInterrogator = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// STATES
	const [browsePath, setBrowsePath] = useState("");
	const [browseFolders, setBrowseFolders] = useState([]);
	const [browseLoading, setBrowseLoading] = useState(false);
	const [browseError, setBrowseError] = useState("");

	const [folderPath, setFolderPath] = useState("");
	const [folderPathError, setFolderPathError] = useState("");
	const [resultsEnabled, setResultsEnabled] = useState(false);

	const [isInterrogating, setIsInterrogating] = useState(false);
	const [progress, setProgress] = useState({
		processed: 0,
		queued: 0,
		currentPath: "/",
		dots: "",
	});

	const [statusRun, setStatusRun] = useState("");
	const [statusError, setStatusError] = useState("");

	const [treeRows, setTreeRows] = useState([]);

	const [folderFilter, setFolderFilter] = useState(() => {
		const saved = localStorage.getItem(FILTER_KEY);
		return saved === "empty" || saved === "not_empty" ? saved : "all";
	});
	const [fileVisibility, setFileVisibility] = useState(() => {
		const saved = localStorage.getItem(FILE_VIS_KEY);
		return saved === "show" ? "show" : "hide";
	});
	const [typeFilters, setTypeFilters] = useState(initialTypeFilters);
	const [fileNameFilter, setFileNameFilter] = useState("");
	const [filtersOpen, setFiltersOpen] = useState(false);

	const [adminOpen, setAdminOpen] = useState(false);
	const [runLogLines, setRunLogLines] = useState(["Log ready."]);

	const cancelRef = useRef({ cancelRequested: false });
	const heartbeatRef = useRef(null);
	const resultsRef = useRef(null);
	const isAtBrowseRoot = displayPath(browsePath) === "/";

	// USE EFFECT
	useEffect(() => {
		loadBrowse("");
	}, []);

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Dropbox Analyzer",
			employeeID: employeeID,
			notes: "Accessed the Dropbox Analyzer page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		localStorage.setItem(FILTER_KEY, folderFilter);
	}, [folderFilter]);

	useEffect(() => {
		localStorage.setItem(FILE_VIS_KEY, fileVisibility);
	}, [fileVisibility]);

	useEffect(() => {
		const normalized = normalizePath(folderPath.trim());
		if (!normalized) {
			setResultsEnabled(false);
			return;
		}

		if (normalized === "/") {
			setResultsEnabled(false);
			return;
		}

		if (isConstrainedRoot(normalized) && !isAllowedPropertyLevelPath(normalized)) {
			setResultsEnabled(false);
			return;
		}

		setResultsEnabled(true);
	}, [folderPath]);

	useEffect(() => {
		return () => {
			if (heartbeatRef.current) {
				clearInterval(heartbeatRef.current);
			}
		};
	}, []);

	// API FUNCTIONS
	const listFolder = async (path) => {
		const dropboxPath = toDropboxApiPath(path);
		const response = await getAPICall(
			instance,
			accounts[0],
			"/api/dropbox/listFolders",
			{ path: dropboxPath }
		);

		const data = response?.data;
		if (!data) {
			throw new Error("Unable to list folder contents.");
		}

		if (
			data.error ||
			data.error_summary ||
			data.error_description ||
			(data.response && data.response.data)
		) {
			throw new Error(
				data.error_summary ||
					data.error_description ||
					data.message ||
					"Unable to list folder contents."
			);
		}

		if (!Array.isArray(data.entries)) {
			throw new Error("Unable to list folder contents.");
		}

		return data;
	};

	// HELPER FUNCTIONS
	const appendLog = (line) => {
		setRunLogLines((prev) => {
			const next = [...prev, `[${new Date().toLocaleTimeString()}] ${line}`];
			if (next.length > 300) {
				next.shift();
			}
			return next;
		});
	};

	const getSelectedTypes = () => {
		if (typeFilters.all) return ["all"];
		return Object.keys(typeFilters).filter(
			(key) => key !== "all" && typeFilters[key]
		);
	};

	const filteredFiles = (row, selectedTypes, normalizedNameFilter) => {
		if (!row.fileNames?.length) return [];
		return row.fileNames.filter((file) => {
			const typeMatch =
				selectedTypes.includes("all") || selectedTypes.includes(file.kind);
			const nameMatch =
				!normalizedNameFilter ||
				file.name.toLowerCase().includes(normalizedNameFilter);
			return typeMatch && nameMatch;
		});
	};

	const countSelected = (row, selectedTypes, normalizedNameFilter) => {
		if (selectedTypes.includes("all") && !normalizedNameFilter) {
			return row.fileCount;
		}
		return filteredFiles(row, selectedTypes, normalizedNameFilter).length;
	};

	const selectedBytes = (row, selectedTypes, normalizedNameFilter) => {
		if (selectedTypes.includes("all") && !normalizedNameFilter) {
			return row.totalBytes;
		}
		return filteredFiles(row, selectedTypes, normalizedNameFilter).reduce(
			(sum, file) => sum + (file.bytes || 0),
			0
		);
	};

	const selectedTypeSummary = (row, selectedTypes, normalizedNameFilter) => {
		const bytesLabel = formatBytes(
			selectedBytes(row, selectedTypes, normalizedNameFilter)
		);
		const count = countSelected(row, selectedTypes, normalizedNameFilter);

		if (selectedTypes.includes("all") || normalizedNameFilter) {
			return `${count} files, ${bytesLabel}`;
		}

		const parts = selectedTypes.map(
			(type) => `${row.typeCounts[type]?.count || 0} ${typeLabelMap[type]}`
		);
		return `${parts.join(", ") || `${count} files`}, ${bytesLabel}`;
	};

	const filteredTreeRows = useMemo(() => {
		const selectedTypes = getSelectedTypes();
		const normalizedNameFilter = fileNameFilter.trim().toLowerCase();

		return treeRows
			.filter((row) => {
				const count = countSelected(row, selectedTypes, normalizedNameFilter);
				if (folderFilter === "empty") return count === 0;
				if (folderFilter === "not_empty") return count > 0;
				return true;
			})
			.filter((row) => {
				if (!normalizedNameFilter) return true;
				return countSelected(row, selectedTypes, normalizedNameFilter) > 0;
			})
			.map((row) => {
				const files = filteredFiles(row, selectedTypes, normalizedNameFilter)
					.slice()
					.sort((a, b) =>
						a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
					);

				return {
					...row,
					selectedCount: countSelected(
						row,
						selectedTypes,
						normalizedNameFilter
					),
					selectedBytes: selectedBytes(
						row,
						selectedTypes,
						normalizedNameFilter
					),
					displaySummary: selectedTypeSummary(
						row,
						selectedTypes,
						normalizedNameFilter
					),
					files,
				};
			});
	}, [treeRows, folderFilter, typeFilters, fileNameFilter]);

	const summaryLabel = useMemo(() => {
		if (!treeRows.length) return "";
		const totalFiles = filteredTreeRows.reduce(
			(sum, row) => sum + row.selectedCount,
			0
		);
		const totalBytes = filteredTreeRows.reduce(
			(sum, row) => sum + row.selectedBytes,
			0
		);
		return `Total files in hierarchy: ${totalFiles}, ${formatBytes(totalBytes)}`;
	}, [treeRows, filteredTreeRows]);

	const interrogateDisabledReason = useMemo(() => {
		if (isInterrogating) return "Interrogation is already running.";

		const normalized = normalizePath(folderPath.trim());
		if (!normalized) return "Select a folder to interrogate.";
		if (normalized === "/")
			return "Select a folder below root to interrogate.";
		if (
			isConstrainedRoot(normalized) &&
			!isAllowedPropertyLevelPath(normalized)
		) {
			return "For /projects and /archives, interrogation is only allowed at /root/oic/property or /root/oic/property/project.";
		}

		return "";
	}, [folderPath, isInterrogating]);

	const progressPercent = useMemo(() => {
		if (!isInterrogating) return 0;
		const totalEstimate = progress.processed + progress.queued + 1;
		if (totalEstimate <= 0) return 0;
		return Math.min(
			100,
			Math.round((progress.processed / totalEstimate) * 100)
		);
	}, [isInterrogating, progress.processed, progress.queued]);

	const folderPathOptions = useMemo(() => {
		const seen = new Set();
		return browseFolders
			.map((folder) => {
				const path = folder.path_display || folder.path_lower || "";
				const name = folder.name || pathBasename(path);
				return {
					path,
					label: name,
					nameLower: name.toLowerCase(),
					searchText: `${name} ${displayPath(path)}`.toLowerCase(),
				};
			})
			.filter((option) => {
				if (!option.path || seen.has(option.path)) return false;
				seen.add(option.path);
				return true;
			});
	}, [browseFolders]);

	const loadBrowse = async (path, options = {}) => {
		setBrowseLoading(true);
		setBrowseError("");
		const normalized = normalizePath(path);
		const showFieldError = Boolean(options.showFieldError);

		try {
			appendLog(`Browse: ${displayPath(normalized)}`);
			const data = await listFolder(normalized);
			const folders = (data.entries || [])
				.filter(
					(entry) =>
						entry[".tag"] === "folder" &&
						!entry.sharing_info?.no_access
				)
				.filter((entry) => {
					if (normalized && normalized !== "/") {
						return true;
					}

					const folderName = (entry.name || "").trim().toLowerCase();
					const pathRoot = splitPathSegments(
						entry.path_display || entry.path_lower || ""
					)[0]?.toLowerCase();
					return (
						allowedRootFolders.has(folderName) ||
						allowedRootFolders.has(pathRoot)
					);
				})
				.sort((a, b) =>
					(a.name || "").localeCompare(b.name || "", undefined, {
						sensitivity: "base",
					})
				);
			setBrowsePath(normalized);
			setFolderPath(displayPathForInput(normalized));
			setFolderPathError("");
			setBrowseFolders(folders);
			return true;
		} catch (error) {
			const message = error?.message || "Failed to load folder list.";
			if (showFieldError) {
				setFolderPathError(
					"Path not found or you do not have access. Enter a valid Dropbox folder path."
				);
			} else {
				setBrowseError(message);
			}
			appendLog(`Browse failed: ${message}`);
			return false;
		} finally {
			setBrowseLoading(false);
		}
	};

	const navigateToTypedPath = async () => {
		const normalized = normalizePath(folderPath.trim());
		if (!normalized) {
			setFolderPathError("Enter a Dropbox folder path.");
			return;
		}

		if (isConstrainedRoot(normalized) && splitPathSegments(normalized).length > 4) {
			setFolderPathError(
				"Navigation blocked. /projects and /archives are limited to /root/oic/property or /root/oic/property/project."
			);
			return;
		}

		setFolderPathError("");
		setStatusError("");
		await loadBrowse(normalized, { showFieldError: true });
	};

	const buildTree = async (rootPath) => {
		const rows = [];
		let totalFiles = 0;
		let totalBytes = 0;
		let processed = 0;
		let heartbeatTick = 0;

		const stack = [
			{
				path: normalizePath(rootPath),
				label: rootPath ? pathBasename(rootPath) : "/",
				depth: 0,
			},
		];

		heartbeatRef.current = setInterval(() => {
			heartbeatTick += 1;
			setProgress((prev) => ({
				...prev,
				dots: ".".repeat((heartbeatTick % 3) + 1),
			}));
		}, 500);

		while (stack.length) {
			if (cancelRef.current.cancelRequested) {
				return { rows, totalFiles, totalBytes, cancelled: true };
			}

			const current = stack.pop();
			setProgress({
				processed,
				queued: stack.length,
				currentPath: displayPath(current.path),
				dots: ".",
			});

			appendLog(`Interrogating: ${displayPath(current.path)}`);
			const data = await listFolder(current.path);
			const entries = data.entries || [];
			const folders = entries.filter((entry) => entry[".tag"] === "folder");
			const files = entries.filter((entry) => entry[".tag"] === "file");
			const typeCounts = countTypes(entries);
			const folderBytes = files.reduce(
				(sum, file) => sum + (typeof file.size === "number" ? file.size : 0),
				0
			);

			rows.push({
				label: current.label,
				depth: current.depth,
				fileCount: files.length,
				totalBytes: folderBytes,
				typeCounts,
				fullPath: current.path || rootPath || "",
				fileNames: files.map((file) => ({
					name: file.name || "",
					kind: classifyFile(file.name || ""),
					bytes: typeof file.size === "number" ? file.size : 0,
					fullPath:
						file.path_display ||
						(current.path
							? `${current.path}/${file.name || ""}`
							: `/${file.name || ""}`),
				})),
			});

			totalFiles += files.length;
			totalBytes += folderBytes;

			// Depth-first traversal keeps each folder's children grouped under it.
			const sortedFolders = folders
				.slice()
				.sort((a, b) =>
					(a.name || "").localeCompare(b.name || "", undefined, {
						sensitivity: "base",
					})
				);
			for (let i = sortedFolders.length - 1; i >= 0; i -= 1) {
				const folder = sortedFolders[i];
				const childPath = folder.path_display || folder.path_lower || "";
				stack.push({
					path: childPath,
					label: folder.name || pathBasename(childPath),
					depth: current.depth + 1,
				});
			}

			if (data.has_more) {
				appendLog(
					`Warning: ${displayPath(current.path)} has more than 2000 entries. Endpoint pagination is not currently exposed.`
				);
			}

			processed += 1;
			setProgress({
				processed,
				queued: stack.length,
				currentPath: displayPath(current.path),
				dots: ".",
			});
		}

		return { rows, totalFiles, totalBytes, cancelled: false };
	};

	const focusResults = () => {
		const target = resultsRef.current;
		if (!target) return;

		target.scrollIntoView({
			behavior: "smooth",
			block: "center",
		});

		if (typeof target.focus === "function") {
			window.setTimeout(() => {
				target.focus({ preventScroll: true });
			}, 100);
		}
	};

	const testConnection = async () => {
		setStatusRun("Testing Dropbox connection...");
		setStatusError("");
		try {
			await listFolder("");
			setStatusRun("Connection is valid.");
			appendLog("Connection test passed.");
		} catch (error) {
			const message = error?.message || "Connection test failed.";
			setStatusError(message);
			appendLog(`Connection test failed: ${message}`);
		}
	};

	// INPUT HANDLERS
	const startInterrogation = async () => {
		setStatusError("");
		setStatusRun("Interrogating folder tree...");
		setTreeRows([]);
		focusResults();

		cancelRef.current.cancelRequested = false;
		setIsInterrogating(true);

		try {
			const pathToRun = normalizePath(folderPath.trim());
			if (
				isConstrainedRoot(pathToRun) &&
				!isAllowedPropertyLevelPath(pathToRun)
			) {
				throw new Error(
					"For /projects and /archives, interrogation is only allowed at /root/oic/property or /root/oic/property/project."
				);
			}
			const result = await buildTree(pathToRun);
			setTreeRows(result.rows);

			if (result.cancelled) {
				setStatusRun("Cancelled. Partial results shown.");
				appendLog("Interrogation cancelled by user.");
			} else {
				setStatusRun("Complete.");
				appendLog(
					`Interrogation complete. Files: ${result.totalFiles}, Bytes: ${formatBytes(result.totalBytes)}`
				);
			}
		} catch (error) {
			const message = error?.message || "Interrogation failed.";
			setStatusError(message);
			setStatusRun("");
			appendLog(`Interrogation failed: ${message}`);
		} finally {
			if (heartbeatRef.current) {
				clearInterval(heartbeatRef.current);
				heartbeatRef.current = null;
			}
			setIsInterrogating(false);
			setProgress((prev) => ({ ...prev, dots: "" }));
		}
	};

	const cancelInterrogation = () => {
		cancelRef.current.cancelRequested = true;
		setStatusRun("Cancelling...");
	};

	const handleTypeAllChange = (checked) => {
		if (checked) {
			setTypeFilters(initialTypeFilters);
			return;
		}
		setTypeFilters((prev) => ({ ...prev, all: false }));
	};

	const handleTypeChange = (key, checked) => {
		setTypeFilters((prev) => {
			const next = { ...prev, [key]: checked };
			const hasSpecific = Object.entries(next).some(
				([filterKey, enabled]) => filterKey !== "all" && enabled
			);
			if (!hasSpecific) {
				next.all = true;
			}
			if (hasSpecific) {
				next.all = false;
			}
			return next;
		});
	};

	// RENDER
	return (
		<div className="DropboxInterrogator">
			<div className="dropboxInterrogatorHeader">
				<div className="dropboxInterrogatorHeaderTitle">
					Dropbox Analyzer
				</div>
			</div>

			<div className="dropboxInterrogatorToolContainer">
				<div className="dropboxInterrogatorBody">
					<div className="dropboxInterrogatorGrid">
					<div className="dropboxInterrogatorCard">
						<Box className="dropboxInterrogatorSelectorHeader">
							<h2 className="dropboxInterrogatorSectionTitle">
								Select a folder to analyze
							</h2>
							<Box className="dropboxInterrogatorSelectorActions">
								<Button
									variant="outlined"
									startIcon={<ArrowUpwardIcon />}
									disabled={isAtBrowseRoot}
									onClick={() =>
										loadBrowse(getParentPath(browsePath))
									}
								>
									Back
								</Button>
								<Button
									variant="outlined"
									startIcon={<RefreshIcon />}
									onClick={() => loadBrowse(browsePath)}
								>
									Refresh
								</Button>
							</Box>
						</Box>

						<div className="dropboxInterrogatorFolderList">
							{browseLoading && (
								<div className="dropboxInterrogatorCentered">
									<CircularProgress size={22} />
								</div>
							)}
							{!browseLoading && browseError && (
								<Typography className="dropboxInterrogatorMutedText">
									{browseError}
								</Typography>
							)}
							{!browseLoading &&
								!browseError &&
								!folderPathError &&
								browseFolders.length === 0 && (
								<Typography className="dropboxInterrogatorMutedText">
									No subfolders.
								</Typography>
							)}
							{!browseLoading && !browseError && browseFolders.length > 0 && (
								<Box className="dropboxInterrogatorFolderItems">
									{browseFolders.map((folder) => (
										<Button
											key={folder.id || folder.path_display || folder.name}
											variant="text"
											className="dropboxInterrogatorFolderItem"
											onClick={() => {
												const nextPath =
													folder.path_display ||
													folder.path_lower ||
													"";
												if (
													isConstrainedRoot(nextPath) &&
													splitPathSegments(nextPath).length > 4
												) {
													appendLog(
														`Blocked navigation past property-level: ${displayPath(nextPath)}`
													);
													setStatusError(
														"Navigation blocked. /projects and /archives are limited to /root/oic/property or /root/oic/property/project."
													);
													return;
												}
												setStatusError("");
												loadBrowse(nextPath);
											}}
										>
											<FolderIcon fontSize="small" />
											<span>{folder.name}</span>
										</Button>
									))}
								</Box>
							)}
						</div>

						<Box className="dropboxInterrogatorPathActionRow">
							<Autocomplete
								freeSolo
								fullWidth
								options={folderPathOptions}
								getOptionLabel={(option) =>
									typeof option === "string" ? option : option.label
								}
								filterOptions={(options, state) => {
									const rawQuery = (state.inputValue || "").trim().toLowerCase();
									if (!rawQuery) return options;
									const lastSegment = rawQuery
										.split("/")
										.filter(Boolean)
										.pop();
									return options.filter((option) =>
										option.searchText.includes(rawQuery) ||
										(Boolean(lastSegment) &&
											option.nameLower.includes(lastSegment))
									);
								}}
								inputValue={folderPath}
								onInputChange={(_event, newInputValue, reason) => {
									if (reason === "input") {
										setFolderPath(newInputValue);
										if (folderPathError) {
											setFolderPathError("");
										}
									}
									if (reason === "clear") {
										setStatusError("");
										setFolderPathError("");
										loadBrowse("");
									}
								}}
								onChange={(_event, selectedOption) => {
									if (
										selectedOption &&
										typeof selectedOption === "object" &&
										selectedOption.path
									) {
										setStatusError("");
										setFolderPathError("");
										loadBrowse(selectedOption.path);
									}
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Dropbox Folder Path"
										onKeyDown={(event) => {
											if (event.key === "Enter") {
												event.preventDefault();
												navigateToTypedPath();
											}
										}}
										error={Boolean(folderPathError)}
										helperText={folderPathError}
										placeholder="/Shared/Projects"
										className="dropboxInterrogatorField"
									/>
								)}
							/>
							<Tooltip
								title={interrogateDisabledReason}
								disableHoverListener={!interrogateDisabledReason}
							>
								<span>
									<Button
										variant="contained"
										startIcon={<PlayArrowIcon />}
										disabled={Boolean(interrogateDisabledReason)}
										onClick={startInterrogation}
									>
										Analyze
									</Button>
								</span>
							</Tooltip>
							{isInterrogating && (
								<Button
									variant="outlined"
									color="error"
									startIcon={<CancelIcon />}
									onClick={cancelInterrogation}
								>
									Cancel
								</Button>
							)}
						</Box>
					</div>

					<div className="dropboxInterrogatorCard" ref={resultsRef} tabIndex={-1}>
						<Box className="dropboxInterrogatorResultsHeader">
							<h2 className="dropboxInterrogatorSectionTitle">
								Analysis Results
							</h2>
							<Button
								variant="outlined"
								onClick={() => setFiltersOpen((prev) => !prev)}
								endIcon={
									filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />
								}
							>
								Filter Results
							</Button>
						</Box>

						<Collapse in={filtersOpen}>
							<Box className="dropboxInterrogatorFilterGroup">
								<Typography className="dropboxInterrogatorFilterTitle">
									Folder Visibility
								</Typography>
								<FormGroup row>
									<FormControlLabel
										control={
											<Checkbox
												checked={folderFilter === "all"}
												onChange={() => setFolderFilter("all")}
												disabled={!resultsEnabled}
											/>
										}
										label="Show All"
									/>
									<FormControlLabel
										control={
											<Checkbox
												checked={folderFilter === "empty"}
												onChange={() => setFolderFilter("empty")}
												disabled={!resultsEnabled}
											/>
										}
										label="Empty"
									/>
									<FormControlLabel
										control={
											<Checkbox
												checked={folderFilter === "not_empty"}
												onChange={() =>
													setFolderFilter("not_empty")
												}
												disabled={!resultsEnabled}
											/>
										}
										label="Not Empty"
									/>
								</FormGroup>
							</Box>

							<Box className="dropboxInterrogatorFilterGroup">
								<Typography className="dropboxInterrogatorFilterTitle">
									File Visibility
								</Typography>
								<FormGroup row>
									<FormControlLabel
										control={
											<Checkbox
												checked={fileVisibility === "hide"}
												onChange={() =>
													setFileVisibility("hide")
												}
												disabled={!resultsEnabled}
											/>
										}
										label="Hide Files"
									/>
									<FormControlLabel
										control={
											<Checkbox
												checked={fileVisibility === "show"}
												onChange={() =>
													setFileVisibility("show")
												}
												disabled={!resultsEnabled}
											/>
										}
										label="Show Files"
									/>
								</FormGroup>
							</Box>

							<Box className="dropboxInterrogatorFilterGroup">
								<Typography className="dropboxInterrogatorFilterTitle">
									File Types
								</Typography>
								<FormGroup row>
									<FormControlLabel
										control={
											<Checkbox
												checked={typeFilters.all}
												onChange={(event) =>
													handleTypeAllChange(
														event.target.checked
													)
												}
												disabled={!resultsEnabled}
											/>
										}
										label="All"
									/>
									{Object.entries(typeLabelMap).map(
										([key, label]) => (
											<FormControlLabel
												key={key}
												control={
													<Checkbox
														checked={typeFilters[key]}
														onChange={(event) =>
															handleTypeChange(
																key,
																event.target.checked
															)
														}
														disabled={!resultsEnabled}
													/>
												}
												label={label}
											/>
										)
									)}
								</FormGroup>
							</Box>

							<Box className="dropboxInterrogatorFileNameRow">
								<TextField
									label="File Name Contains"
									fullWidth
									value={fileNameFilter}
									onChange={(event) =>
										setFileNameFilter(event.target.value)
									}
									disabled={!resultsEnabled}
								/>
								<Button
									variant="outlined"
									startIcon={<ClearIcon />}
									disabled={!resultsEnabled}
									onClick={() => setFileNameFilter("")}
								>
									Clear
								</Button>
							</Box>
						</Collapse>

						{filtersOpen && <Divider />}

						{summaryLabel && (
							<Typography className="dropboxInterrogatorTreeSummary">
								{summaryLabel}
							</Typography>
						)}

						<div className="dropboxInterrogatorTree">
							{isInterrogating && (
								<>
									<div className="dropboxInterrogatorLoadingBarContainer">
										<LinearProgress
											variant="determinate"
											value={progressPercent}
										/>
										<Typography className="dropboxInterrogatorTreeMessage">
											Processing folders... {progressPercent}%
										</Typography>
									</div>
									<Typography className="dropboxInterrogatorTreeMessage">
										Working{progress.dots}
										<br />
										Current folder: {progress.currentPath}
										<br />
										Folders processed: {progress.processed}
										<br />
										Folders queued: {progress.queued}
									</Typography>
								</>
							)}

							{!isInterrogating && treeRows.length === 0 && (
								<Typography className="dropboxInterrogatorTreeMessage">
									No results yet.
								</Typography>
							)}

							{!isInterrogating &&
								treeRows.length > 0 &&
								(filteredTreeRows.length === 0 ? (
									<Typography className="dropboxInterrogatorTreeMessage">
										No results found for the current filters.
									</Typography>
								) : (
									filteredTreeRows.map((row) => (
										<Box
											key={`${row.fullPath}-${row.depth}`}
											className="dropboxInterrogatorTreeRow"
											sx={{ pl: `${row.depth * 2}ch` }}
										>
											<Typography
												className={
													row.selectedCount > 0
														? "dropboxInterrogatorHasFiles"
														: "dropboxInterrogatorEmpty"
												}
											>
												•
												<a
													href={buildDropboxFolderUrl(
														row.fullPath
													)}
													target="_blank"
													rel="noopener noreferrer"
													className="dropboxInterrogatorFolderLink"
												>
													{row.label}
												</a>
												({row.displaySummary})
											</Typography>

											{fileVisibility === "show" &&
												row.files.map((file) => (
													<Typography
														key={`${row.fullPath}-${file.fullPath}`}
														className="dropboxInterrogatorFileRow"
														sx={{
															pl: `${(row.depth + 1) * 2}ch`,
														}}
													>
														<DescriptionIcon fontSize="inherit" />
														<a
															href={buildDropboxPreviewUrl(
																file.fullPath
															)}
															target="_blank"
															rel="noopener noreferrer"
															className="dropboxInterrogatorFileLink"
														>
															{file.name}
														</a>
													</Typography>
												))}
										</Box>
									))
								))}
						</div>

						<Typography className="dropboxInterrogatorFootnote">
							Counts represent files directly in each folder (not recursive).
						</Typography>
					</div>

					{props.isAdmin && (
						<div className="dropboxInterrogatorCard">
							<Box className="dropboxInterrogatorAdminHeader">
								<h2 className="dropboxInterrogatorSectionTitle">
									Admin Panel
								</h2>
								<Button
									variant="contained"
									startIcon={<BugReportIcon />}
									onClick={() => setAdminOpen((prev) => !prev)}
								>
									Admin
									{adminOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								</Button>
							</Box>

							{adminOpen && (
								<>
									<Box className="dropboxInterrogatorButtonRow">
										<Button
											variant="outlined"
											onClick={testConnection}
										>
											Test Connection
										</Button>
										<Button
											variant="outlined"
											onClick={() =>
												setRunLogLines(["Log ready."])
											}
										>
											Clear Log
										</Button>
									</Box>

									{statusRun && (
										<Alert severity="info">{statusRun}</Alert>
									)}
									{statusError && (
										<Alert severity="error">
											{statusError}
										</Alert>
									)}

									<Typography className="dropboxInterrogatorFootnote">
										Dropbox authentication is managed by
										j2-tools server session.
									</Typography>

									<div className="dropboxInterrogatorLog">
										{runLogLines.join("\n")}
									</div>
								</>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
		</div>
	);
};

// EXPORT
export default DropboxInterrogator;


